import { AdvisorsPaths } from '@src/components/sco/Advisors/paths'
import {
  AGREEMENT_STATUS_FILTERS,
  type AgreementStatusFilter,
} from '@src/components/sco/Advisors/utils/agreementList'
import { IconType } from 'react-icons'
import { LuBadgeCheck, LuCircleX, LuClock, LuFiles, LuSquarePen } from 'react-icons/lu'

export type AgreementsNavItem = {
  key: AgreementStatusFilter
  title: string
  description: string
  href: string
  icon: IconType
  palette?: string
}

export const AGREEMENT_STATUS_FILTER_LABELS: Record<AgreementStatusFilter, string> = {
  all: 'All',
  draft: 'Drafts',
  sent: 'Pending Signature',
  signed: 'Signed',
  declined: 'Declined',
}

export const AGREEMENT_STATUS_FILTER_COPY: Record<
  AgreementStatusFilter,
  { emptyTitle: string; emptyBody: string; cta?: string }
> = {
  all: {
    emptyTitle: 'No agreements yet',
    emptyBody:
      'Create a FAST agreement for an advisor. You can attach a board seat later, or send terms by email. Incoming agreements from founders also show up here.',
    cta: 'Create a New Agreement',
  },
  draft: {
    emptyTitle: 'No drafts',
    emptyBody: 'Start a FAST agreement here. You can send it for signature when the terms are ready.',
    cta: 'Create a New Agreement',
  },
  sent: {
    emptyTitle: 'Nothing waiting on a signature',
    emptyBody: 'After you send terms, they wait here until both parties sign in Documenso.',
  },
  signed: {
    emptyTitle: 'No signed agreements',
    emptyBody: 'Completed FAST terms land here, including the signed PDF.',
  },
  declined: {
    emptyTitle: 'No declined agreements',
    emptyBody: 'Terms someone declined appear here so you can revise or delete them.',
  },
}

export const parseAgreementStatusFilter = (value: unknown): AgreementStatusFilter => {
  const raw = String(Array.isArray(value) ? value[0] : (value ?? '')).toLowerCase()
  return (AGREEMENT_STATUS_FILTERS as readonly string[]).includes(raw)
    ? (raw as AgreementStatusFilter)
    : 'all'
}

export const agreementStatusFilterHref = (paths: AdvisorsPaths, filter: AgreementStatusFilter): string =>
  paths.withQuery(paths.agreements, { status: filter === 'all' ? undefined : filter })

/**
 * FAST Agreements sub-sections, in rail order.
 *
 * Status views are the list filters. Create a New Agreement is the rail CTA.
 */
export const agreementsNavItems = (paths: AdvisorsPaths): AgreementsNavItem[] => [
  {
    key: 'all',
    title: AGREEMENT_STATUS_FILTER_LABELS.all,
    description: 'FAST equity terms you have drafted, sent, or been asked to sign.',
    href: agreementStatusFilterHref(paths, 'all'),
    icon: LuFiles,
    palette: 'green',
  },
  {
    key: 'draft',
    title: AGREEMENT_STATUS_FILTER_LABELS.draft,
    description: 'Terms you have started but not sent for signature.',
    href: agreementStatusFilterHref(paths, 'draft'),
    icon: LuSquarePen,
    palette: 'purple',
  },
  {
    key: 'sent',
    title: AGREEMENT_STATUS_FILTER_LABELS.sent,
    description: 'Sent FAST terms waiting on a founder or advisor signature.',
    href: agreementStatusFilterHref(paths, 'sent'),
    icon: LuClock,
    palette: 'yellow',
  },
  {
    key: 'signed',
    title: AGREEMENT_STATUS_FILTER_LABELS.signed,
    description: 'Completed FAST terms, including the signed PDF.',
    href: agreementStatusFilterHref(paths, 'signed'),
    icon: LuBadgeCheck,
    palette: 'green',
  },
  {
    key: 'declined',
    title: AGREEMENT_STATUS_FILTER_LABELS.declined,
    description: 'Terms someone declined. You can revise or delete them.',
    href: agreementStatusFilterHref(paths, 'declined'),
    icon: LuCircleX,
    palette: 'red',
  },
]
